// Vendor Imports
import { bindAll, isEqual, noop, uniqueId as _uniqueId, cloneDeep } from 'lodash';
import React, { Component } from 'react';
import { ForgeIcon, ForgeIconButton, ForgePopover, ForgeTextField } from '@tylertech/forge-react';
import { type IPopoverComponent } from '@tylertech/forge';
import { type IPopoverToggleEventData } from '@tylertech/forge/esm/popover';

// Project Imports
import FilterEditor from './FilterEditor';
import FilterConfig from './FilterConfig';
import * as BaseFilter from './lib/Filters/BaseFilter';
import { I18n } from 'common/i18n';
import { isInsideFlannels, defaultRelativeDateOptions } from './lib/helpers';

// Constants
import { FilterDataType, type SoqlFilter } from './SoqlFilter';
import {
  type DataProvider,
  type RelativeDatePeriod,
  type FilterBarPendoIds,
  type FilterColumnsMap,
} from 'common/components/FilterBar/types';
import { type ParameterConfiguration } from 'common/types/reportFilters';
import { Key } from 'common/types/keyboard/key';

import { getFilterHumanText } from './lib/Filters';
import LabelWithHelperText from 'common/components/LabelWithHelperText';

export interface FilterItemProps {
  allFilters: SoqlFilter[];
  allParameters: ParameterConfiguration[];
  constraints?: {
    geoSearch: {
      boundary: number[];
    };
  };
  appliedFilter: SoqlFilter;
  columns: FilterColumnsMap;
  disabled?: boolean;
  relativeDateOptions?: RelativeDatePeriod[];
  isReadOnly?: boolean;
  dataProvider: DataProvider[];
  onRemove: () => void;
  onUpdate: (filter: SoqlFilter) => void;
  onToggleControl: (value: boolean) => void;
  isMeasureInStory: boolean;
  showNullsAsFalse?: boolean;
  showRemoveButtonInFilterEditor?: boolean;
  pendoIds?: FilterBarPendoIds;
  independentTextFilters?: boolean;
  tabIndex?: number;
}

interface FilterItemState {
  isControlOpen: boolean;
  isConfigOpen: boolean;
  columnStatsLoaded: boolean;
  uniqueId: string;
  columnsWithColumnStats: FilterColumnsMap;
}

function FilterLabel({
  filterName,
  uniqueId,
  isFilterDisabled,
  showMeasureInfoWithDateFilter,
}: {
  filterName: string;
  uniqueId: string;
  isFilterDisabled: boolean | undefined;
  showMeasureInfoWithDateFilter: boolean;
}): JSX.Element {
  if (isFilterDisabled || showMeasureInfoWithDateFilter) {
    const popoverContent = isFilterDisabled
      ? I18n.t('shared.components.filter_bar.filter_tooltip')
      : I18n.t('shared.components.filter_bar.measures_support_text');
    const popoverIconId = isFilterDisabled ? `${uniqueId}-disabled-helper` : `${uniqueId}-label-helper`;

    return (
      <span slot="label">
        <LabelWithHelperText
          ariaLabel={filterName}
          className="filter-label-with-helper"
          label={filterName}
          popoverContent={popoverContent}
          popoverIconId={popoverIconId}
          popoverMaxWidth={225}
        />
      </span>
    );
  }

  return (
    <label className="filter-label" slot="label">
      {filterName}
    </label>
  );
}

export class FilterItem extends Component<FilterItemProps, FilterItemState> {
  static defaultProps = {
    onToggleControl: noop,
  };

  private _isMounted: boolean;
  private filterControlToggle = React.createRef<HTMLInputElement>();
  private filterControlPopupRef = React.createRef<HTMLElement & IPopoverComponent>();
  private filterConfig = React.createRef<FilterConfig>();
  private filterConfigToggle = React.createRef<HTMLDivElement>();
  private filterConfigPopupRef = React.createRef<HTMLElement & IPopoverComponent>();
  private controlContainer = React.createRef<HTMLDivElement>();
  private configContainer = React.createRef<HTMLDivElement>();

  bodyClickHandler: (e: MouseEvent) => void;
  bodyEscapeHandler: (e: KeyboardEvent) => void;

  getDataProvider(props: FilterItemProps) {
    return props.dataProvider[0];
  }

  constructor(props: FilterItemProps) {
    super(props);
    this.state = {
      isControlOpen: false,
      isConfigOpen: false,
      columnStatsLoaded: false,
      uniqueId: _uniqueId('filter-item-'),
      columnsWithColumnStats: this.props.columns,
    };

    bindAll(this, [
      'controlPopoverToggle',
      'onFocusConfigToggle',
      'onKeyDownControl',
      'onRemove',
      'onUpdate',
      'renderFilterConfig',
      'renderFilterConfigToggle',
      'renderFilterControl',
      'toggleConfig',
      'toggleControl',
    ]);
  }

  componentDidMount() {
    // To prevent a race condition updating state on column stats when fetching is completed after the component is unmounted
    this._isMounted = true;
    this.updateColumnsWithStats();
    this.bodyClickHandler = (event) => {
      const { isConfigOpen } = this.state;
      if (!isConfigOpen) {
        return;
      }

      // Avoid closing flannels if the click is inside any of these refs.
      const flannelElements = [this.filterConfig.current, this.filterConfigToggle.current] as (
        | HTMLDivElement
        | FilterConfig
        | null
      )[];

      const clickInsideFlannels = isInsideFlannels(event, flannelElements);

      // If none of the flannelElements contain event.target, close all the flannels.
      if (!clickInsideFlannels) {
        this.closeConfig();
      }
    };

    this.bodyEscapeHandler = (event) => {
      const { isConfigOpen, isControlOpen } = this.state;

      if (event.key === Key.Escape) {
        if (isConfigOpen && this.filterConfigToggle.current) {
          this.closeConfig();
          this.filterConfigToggle.current.focus();
        }
        if (isControlOpen && this.filterControlToggle.current) {
          this.closeControl();
          this.filterControlToggle.current.focus();
        }
      }
    };

    document.body.addEventListener('click', this.bodyClickHandler);
    document.body.addEventListener('keyup', this.bodyEscapeHandler);
  }

  componentWillUnmount() {
    this._isMounted = false;
    document.body.removeEventListener('click', this.bodyClickHandler);
    document.body.removeEventListener('keyup', this.bodyEscapeHandler);
  }

  componentDidUpdate(nextProps: FilterItemProps) {
    if (!isEqual(nextProps.appliedFilter.columns, this.props.appliedFilter.columns)) {
      // If the set of columns included in the filter has changed or the filter config has
      // changed, refetch column stats and update the filter in state
      this.updateColumnsWithStats();
    }
  }

  columnStatLoaded = (): boolean => {
    if (BaseFilter.isColumnStatRequired(this.props.appliedFilter)) {
      return this.state.columnStatsLoaded;
    }
    return true;
  };

  /**
   * Keep the columnsWithColumnStats in sync with what's passed through props.
   * Fetches column stats for relevant filter types as well.
   */
  updateColumnsWithStats = async (props = this.props): Promise<void> => {
    const { appliedFilter, columns, dataProvider } = props;

    if (!columns || !this._isMounted) {
      return;
    }

    if (!BaseFilter.isColumnStatRequired(appliedFilter)) {
      this.setState({ columnsWithColumnStats: columns });
      return;
    }

    try {
      this.setState({ columnStatsLoaded: false });
      const columnsWithColumnStats = await BaseFilter.getAllColumnStats(dataProvider, columns);
      if (this._isMounted) {
        this.setState({ columnStatsLoaded: true, columnsWithColumnStats });
      }
    } catch (error) {
      if (this._isMounted) {
        this.setState({ columnStatsLoaded: false });
      }
      if (columns) {
        console.error(
          `Soql like cachedContents failed for one or more of data sources: ${Object.keys(columns).join(
            ', ',
          )}`,
        );
      }
      console.error(error);
    }
  };

  onKeyDownControl(event: React.KeyboardEvent): void {
    const { disabled, appliedFilter } = this.props;
    const openKeys = [Key.ArrowDown, Key.Enter, Key.Space];

    if (disabled || appliedFilter.isOverridden) {
      if (openKeys.includes(event.key as Key)) {
        event.stopPropagation();
        event.preventDefault();
      }
      return;
    }

    if (openKeys.includes(event.key as Key)) {
      event.stopPropagation();
      event.preventDefault();
      this.toggleControl();
    }
  }

  onUpdate(newFilter: SoqlFilter, { shouldCloseControl = true } = {}) {
    this.props.onUpdate(newFilter);
    if (shouldCloseControl && this.filterControlToggle.current) {
      this.filterControlToggle.current.focus();
      this.closeControl();
    }
  }

  onRemove(): void {
    this.props.onRemove();
  }

  toggleControl(): void {
    const { onToggleControl } = this.props;
    onToggleControl(!this.state.isControlOpen);

    this.setState({
      isControlOpen: !this.state.isControlOpen,
      isConfigOpen: false,
    });
  }

  toggleConfig(): void {
    const { onToggleControl } = this.props;
    onToggleControl(false);

    this.setState({
      isControlOpen: false,
      isConfigOpen: !this.state.isConfigOpen,
    });
  }

  closeConfig(): void {
    const { isConfigOpen } = this.state;
    if (!isConfigOpen) {
      return;
    }

    this.setState({
      isConfigOpen: false,
    });
  }

  closeControl(): void {
    this.setState({
      isControlOpen: false,
    });
  }

  controlPopoverToggle(event: IPopoverToggleEventData): void {
    // Forge owns the control popover's open/close (click trigger + light dismiss); mirror it into state.
    const isControlOpen = event.newState === 'open';
    if (isControlOpen !== this.state.isControlOpen) {
      this.props.onToggleControl(isControlOpen);
      this.setState({ isControlOpen, isConfigOpen: isControlOpen ? false : this.state.isConfigOpen });
    }
    // Only pull focus back to the trigger if focus wasn't claimed elsewhere (e.g. clicking another filter).
    if (!isControlOpen && (!document.activeElement || document.activeElement === document.body)) {
      this.filterControlToggle.current?.focus();
    }
  }

  onFocusConfigToggle() {
    const { isControlOpen } = this.state;
    // When tabbing out of the filter control to the filter config, close the filter control popup.
    if (isControlOpen && this.filterConfigToggle.current) {
      this.closeControl();
    }
  }

  renderFilterConfig(): JSX.Element | null {
    const { appliedFilter, onUpdate } = this.props;
    const { isConfigOpen } = this.state;

    if (!isConfigOpen) {
      return null;
    }

    const configProps = {
      filter: appliedFilter,
      onUpdate,
    };

    // The popover position changes whether or not the filters are rendering in a GFB, so we will use the pendoIds prop to
    // determine the position of the popover because those are only passed in when the filters are rendering in a GFB.
    const popoverPosition = this.props.pendoIds?.expandButton ? 'bottom-start' : 'bottom-end';

    return (
      <ForgePopover
        ref={this.filterConfigPopupRef}
        open={isConfigOpen}
        placement={popoverPosition}
        anchorElement={this.filterConfigToggle.current}
      >
        <FilterConfig {...configProps} ref={this.filterConfig} />
      </ForgePopover>
    );
  }

  renderFilterConfigToggle(): JSX.Element | null {
    const { isReadOnly, disabled } = this.props;
    const { isConfigOpen, uniqueId } = this.state;

    if (isReadOnly || disabled) {
      return null;
    }

    return (
      <ForgeIconButton
        aria-controls={isConfigOpen ? `${uniqueId}-config` : undefined}
        aria-expanded={isConfigOpen}
        aria-haspopup="dialog"
        aria-label={I18n.t('shared.components.filter_bar.configure_filter')}
        className="filter-config-toggle"
        density="small"
        onClick={this.toggleConfig}
        onFocus={this.onFocusConfigToggle}
        ref={this.filterConfigToggle}
        shape="squared"
      >
        <ForgeIcon name="more_vert" />
      </ForgeIconButton>
    );
  }

  renderFilterControl(): JSX.Element | null {
    const { isControlOpen, uniqueId, columnsWithColumnStats } = this.state;
    const {
      appliedFilter,
      constraints,
      allFilters,
      allParameters,
      relativeDateOptions,
      isReadOnly,
      showNullsAsFalse,
      dataProvider,
      disabled,
      showRemoveButtonInFilterEditor = false,
      independentTextFilters = false,
    } = this.props;

    if (disabled || appliedFilter.isOverridden) {
      return null;
    }

    const isOpen = isControlOpen && this.columnStatLoaded();

    const relativeDateOptionsWithOverrides = relativeDateOptions
      ? relativeDateOptions
      : defaultRelativeDateOptions();

    const filterProps = {
      appliedFilter: cloneDeep(appliedFilter),
      columns: cloneDeep(columnsWithColumnStats),
      constraints,
      allFilters,
      allParameters,
      relativeDateOptions: relativeDateOptionsWithOverrides,
      isReadOnly,
      showNullsAsFalse,
      dataProvider,
      onRemove: this.onRemove,
      onUpdate: this.onUpdate,
      popupRef: this.filterControlPopupRef,
      showRemoveButtonInFooter: showRemoveButtonInFilterEditor,
      independentTextFilters,
    };

    // The popover position changes whether or not the filters are rendering in a GFB, so we will use the pendoIds prop to
    // determine the position of the popover because those are only passed in when the filters are rendering in a GFB.
    const popoverPosition = this.props.pendoIds?.expandButton ? 'bottom-start' : 'bottom-end';

    return (
      <ForgePopover
        ref={this.filterControlPopupRef}
        open={isOpen}
        anchor={`${uniqueId}-control-anchor`}
        placement={popoverPosition}
        on-forge-popover-toggle={(event: CustomEvent) => this.controlPopoverToggle(event.detail)}
      >
        <div id={`${uniqueId}-control`}>{isOpen && <FilterEditor {...filterProps} />}</div>
      </ForgePopover>
    );
  }

  render() {
    const { appliedFilter, columns, disabled, isMeasureInStory, pendoIds, showNullsAsFalse } = this.props;
    const { isControlOpen, uniqueId, columnsWithColumnStats } = this.state;

    const filterName = BaseFilter.getFilterName(appliedFilter, columns);
    const isFilterDisabled = disabled || appliedFilter.isOverridden;
    const filterHumanText = getFilterHumanText(appliedFilter, columnsWithColumnStats, showNullsAsFalse);
    const showMeasureInfoWithDateFilter =
      isMeasureInStory && appliedFilter.dataTypeName === FilterDataType.DATE;

    return (
      <div className="filter-bar-filter">
        <div
          className="filter-control-container"
          data-testid="filter-bar-filter-control-container"
          id={pendoIds?.filterParameterControlContainer}
          ref={this.controlContainer}
        >
          <ForgeTextField
            className="filter-item"
            id={`${uniqueId}-control-anchor`}
            density="small"
            aria-disabled={isFilterDisabled || undefined}
            label-position="block-start"
            popoverIcon={!isFilterDisabled}
            variant="filled"
          >
            <FilterLabel
              filterName={filterName}
              uniqueId={uniqueId}
              isFilterDisabled={isFilterDisabled}
              showMeasureInfoWithDateFilter={showMeasureInfoWithDateFilter}
            />
            <input
              ref={this.filterControlToggle}
              aria-label={`${I18n.t('shared.components.filter_bar.filter')} ${filterName} - ${filterHumanText}`}
              aria-haspopup={isFilterDisabled ? undefined : 'dialog'}
              aria-expanded={isFilterDisabled ? false : isControlOpen}
              aria-controls={!isFilterDisabled && isControlOpen ? `${uniqueId}-control` : undefined}
              aria-readonly="true"
              readOnly
              role="combobox"
              value={filterHumanText}
              onKeyDown={this.onKeyDownControl}
            />
          </ForgeTextField>
          {this.renderFilterControl()}
          {this.renderFilterConfigToggle()}
          {this.renderFilterConfig()}
        </div>
      </div>
    );
  }
}
